/**
 * Concurrency helpers for rule creation, shared by the Entry (opener) and
 * Unwinding pages — the two are deliberate mirrors.
 *
 * A rule-less row has several gestures that can create its rule (param
 * ✓/Enter, a maker switch, the TT switch on Entry; the Kalshi/Polymarket
 * toggles on Unwinding). Before these helpers, each gesture POSTed
 * independently: pressing one and then another ~1s later (before the rules
 * refetch landed) fired a SECOND create for the same pair, leaving a
 * permanent orphan duplicate rule. The invariant now is: at most ONE create
 * in flight per pair — every later gesture joins that create's promise and
 * applies its flag via the normal PATCH path on the resolved rule.
 */

export interface SingleFlight<T> {
  /**
   * Returns the in-flight promise for `key` if one exists; otherwise
   * starts `create()` and registers its promise under `key` until it
   * settles (success or failure — a failed create must be retryable).
   */
  ensure: (key: string, create: () => Promise<T>) => Promise<T>;
  inFlight: (key: string) => boolean;
}

export function createSingleFlight<T>(): SingleFlight<T> {
  const pending = new Map<string, Promise<T>>();
  return {
    ensure(key, create) {
      const existing = pending.get(key);
      if (existing) return existing;
      const entry: Promise<T> = create().finally(() => {
        if (pending.get(key) === entry) pending.delete(key);
      });
      pending.set(key, entry);
      return entry;
    },
    inFlight: key => pending.has(key)
  };
}

/**
 * Duplicate-create conflict reported by the backend: POST /rules answers
 * HTTP 409 with `{ message, existingRuleId }` when the pair already has a
 * rule. Returns the conflict payload, or null when the error is anything
 * else (caller rethrows).
 */
export function extractDuplicateRuleConflict(
  err: unknown
): { existingRuleId: string } | null {
  const response = (
    err as {
      response?: { status?: number; data?: { existingRuleId?: unknown } };
    } | null
  )?.response;
  if (!response || response.status !== 409) return null;
  const existingRuleId = response.data?.existingRuleId;
  if (typeof existingRuleId !== 'string' || !existingRuleId) return null;
  return { existingRuleId };
}

/**
 * 409 recovery for the create mutation: a duplicate-create conflict means
 * "the rule already exists", so resolve to that rule (via `fetchRule`)
 * instead of surfacing a failure — the caller continues with its PATCH
 * exactly as if this call had created the rule. Any other error rethrows
 * untouched.
 */
export async function recoverFromDuplicateCreate<T>(
  err: unknown,
  fetchRule: (id: string) => Promise<T>
): Promise<T> {
  const conflict = extractDuplicateRuleConflict(err);
  if (!conflict) throw err;
  return fetchRule(conflict.existingRuleId);
}

/**
 * Cache seeding for the create mutation's onSuccess: computes the updated
 * rule lists so the freshly created rule is visible the moment the create
 * resolves. Invalidation alone left a ~1-5s window where rows still saw
 * `rule === undefined` and a second gesture fired a duplicate create.
 *
 * `lists` is `queryClient.getQueriesData(...)` output: `[key, data]` pairs
 * where an array key's second element is the list's ruleType filter ('all'
 * for the unfiltered list). The rule is seeded into the 'all' list and the
 * list matching its ruleType; `defaultRuleType` supplies the type for
 * legacy docs that lack one (the unwinder defaults those to 'tna').
 * Lists that already contain the rule (or hold no data) are skipped.
 */
export function seedRuleIntoLists<
  T extends { _id: string; ruleType?: string },
  K
>(
  lists: ReadonlyArray<readonly [K, T[] | undefined]>,
  created: T,
  opts?: { defaultRuleType?: string }
): Array<[K, T[]]> {
  const createdType = created.ruleType ?? opts?.defaultRuleType;
  const updates: Array<[K, T[]]> = [];
  for (const [key, data] of lists) {
    const keyRuleType = Array.isArray(key) ? key[1] : 'all';
    if (keyRuleType !== 'all' && createdType && keyRuleType !== createdType) {
      continue;
    }
    if (!Array.isArray(data)) continue;
    if (data.some(r => r._id === created._id)) continue;
    updates.push([key, [...data, created]]);
  }
  return updates;
}
